import * as React from 'react';
import {ICustomerPortalService, ProductsEnum, Quote, resourcesHelper, Types} from "@falcon/services";
import {PluginTemplates} from "../../../../utils/PluginTemplates";
import {lazyInject} from "@athena-portal/core";
import {Link} from "react-router-dom";

interface IState {
    annualMotorQuotes: Quote[];
    annualHomeQuotes: Quote[];
}

/*
 * This is an abstract base class for the 2 new quote pages that we have: when there is an existing
 * policy and when there is no policy. The descendant quote pages are very similar, except for LV=
 * product marketing and popups.
 */
export abstract class NewQuotePageBase<P = {}, S extends IState = IState> extends React.Component<P, S> {
    protected pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Get a new quote or view saved quotes");
        this.fetchQuotes();
    }

    private readonly fetchQuotes = () => {
        this.customerPortalService.getQuotes().subscribe((quotes: Quote[]) => {
            this.setState({
                annualMotorQuotes: this.getQuotesByProduct(quotes, ProductsEnum.WOLF_MOTOR),
                annualHomeQuotes: this.getQuotesByProduct(quotes, ProductsEnum.HOME)
            });
        });
    }

    private getQuotesByProduct(quotes: Quote[], product: string): Quote[] {
        return quotes?.filter((quote) => quote?.product === product);
    }

    protected renderMotorQuoteTile = (quoteCount: number) => { // NOSONAR
        return (
            <>
                {this.renderQuoteCountWording(quoteCount)}
                <div className="content-above-button">
                    <div className="logo-grid">
                        <img className="car-logo" src={`/${resourcesHelper.THEME}/img/wolf-car-icon.png`}
                             alt="car icon"/>
                        <img className="defaqto-logo" src="/img/DefaqtoLogo.svg" alt="Defaqto logo"
                             aria-label="Defaqto logo"/>
                    </div>
                    <div className="section overflow-hidden text-center">
                        <div className="product-title">{resourcesHelper.PRODUCT} Car Insurance</div>
                        {PluginTemplates.getNewQuoteWording[ProductsEnum.WOLF_MOTOR]}
                    </div>
                </div>
            </>
        );
    }

    protected renderHomeQuoteTile = (quoteCount: number) => { // NOSONAR
        return (
            <>
                {this.renderQuoteCountWording(quoteCount)}
                <div className="content-above-button">
                    <div className="logo-grid">
                        <img className="home-logo" src={`/${resourcesHelper.THEME}/img/home-icon.png`}
                             alt="home icon"/>
                        <img className="defaqto-logo" src="/img/DefaqtoLogoHome.svg" alt="Defaqto logo"
                             aria-label="Defaqto logo"/>
                    </div>
                    <div className="section overflow-hidden text-center">
                        <div className="product-title">{resourcesHelper.PRODUCT} Home Insurance</div>
                        {PluginTemplates.getNewQuoteWording[ProductsEnum.HOME]}
                    </div>
                </div>
            </>
        );
    }

    protected renderGetNewQuoteLink = (quoteCount: number, linkToNbJourney: string) => { // NOSONAR
        const hasHomeQuotes: boolean = quoteCount > 0;
        return (
            <div className="quote-panel-view-quotes">
                {
                    hasHomeQuotes
                        ? <Link className="text-decoration-underline cursor-pointer muli-bold-16" to={linkToNbJourney}>
                            Get a new home quote
                        </Link>
                        : <p className="mb-0">You don’t have any home quotes</p>
                }
            </div>
        );
    }

    private readonly renderQuoteCountWording = (quoteCount: number) => {
        const quoteText: string = quoteCount === 1 ? " quote" : " quotes";
        return (
            <div className="valid-quote-counter">
                <p>You have <span className="muli-bold-16">{quoteCount}</span> {quoteText}</p>
            </div>
        );
    }

    protected renderRenewalDateCapturePanel = () => { // NOSONAR
        const renewalCapturePrizeDrawEnabled: boolean = process.env.REACT_APP_RENEWAL_DATE_CAPTURE_PRIZE_DRAW_ENABLED ? JSON.parse(process.env.REACT_APP_RENEWAL_DATE_CAPTURE_PRIZE_DRAW_ENABLED) : false;
        return (
            <div className="banner banner-content">
                <img src={`/${resourcesHelper.THEME}/img/Flow-info.svg`} alt="Blue info icon"/>
                <div className="banner-text">
                    <span className="font-weight-bold">Not the right time for a quote?</span>
                    {renewalCapturePrizeDrawEnabled ? (
                        <p className="mt-3">If you have a policy with another insurer, just let us know when it's due for
                            renewal for a chance to <span className="font-weight-bold">win a £150 Amazon voucher</span> in our monthly prize draw.{' '}
                            <a href={"https://cloud.comms.allianz.co.uk/accountrdctcs"}
                               className="text-decoration-underline muli-bold-16-blue">T&Cs apply</a>.
                        </p>
                        ) : (
                        <p className="mt-3">If you have a policy with another insurer, just let us know when it's due for
                            renewal.
                        </p>
                        )}
                    <p className="mt-3">We'll get in touch before your renewal date so you can get a quote from us and
                        compare it with
                        your current provider.{' '}
                        <a href={resourcesHelper.LET_US_KNOW_URL} id="renewal-date-capture-link">Let
                            us know</a>
                    </p>
                </div>
            </div>
        )
    }
}
